/**
 * B403 #37 — the expired-cart self-heal, as ONE seam.
 *
 * A remembered cartId the server no longer holds (TTL'd, abandoned
 * elsewhere) must not trap the buyer in a refusal loop: the wire's exact
 * "No such cart" refusal retires the stored id and the SAME press replays
 * once on a fresh cart. Only that refusal heals — every other refusal
 * (and a second "No such cart" on the fresh cart) still surfaces.
 *
 * The orchestration lives here rather than inline in App.tsx so the
 * composed no-such-cart-heals walls (test/cartHeal.test.ts) drive the
 * very unit the container spends — a wall over a copy is not a wall.
 */
import { ApiError } from "./api.js";

/** The one refusal that self-heals: the wire's own sentence, exactly. */
export function isNoSuchCart(error: unknown): boolean {
  return error instanceof ApiError && error.message === "No such cart";
}

/**
 * B545 (e) — in the booking DIALOG a remembered cart that is already
 * CHECKED OUT is just as stale for adding: the sheet reopened over it
 * (the frame boots fresh each open), and the resume read did not claim it.
 * Healing it opens a fresh cart instead of showing the buyer "already on
 * its way to payment". The hosted page keeps the refusal (its flow owns
 * the pending order in memory).
 */
export function isStaleForAdding(error: unknown, opts: { readonly healCheckedOut: boolean }): boolean {
  if (isNoSuchCart(error)) return true;
  return opts.healCheckedOut && error instanceof ApiError && error.message === "Cart is checked out and no longer accepts changes";
}

export interface CartHealDeps {
  /** Open (or re-open) a cart, returning its id — App.tsx's openCart. */
  readonly open: () => Promise<string>;
  /** Retire the remembered id — session.clearCartId. */
  readonly clear: () => void;
  /** B545 (e) — also heal a checked-out cart (the dialog walk). */
  readonly healCheckedOut?: boolean;
}

/**
 * Run `press` against the open cart; on the exact "No such cart" refusal,
 * retire the stored id and replay the SAME press once on a fresh cart.
 * The heal replays ONCE — a refusal on the fresh cart surfaces.
 */
export async function runWithCartHeal(
  deps: CartHealDeps,
  press: (cartId: string) => Promise<void>,
): Promise<void> {
  const first = await deps.open();
  try {
    await press(first);
  } catch (error) {
    if (!isStaleForAdding(error, { healCheckedOut: deps.healCheckedOut === true })) throw error;
    deps.clear();
    const fresh = await deps.open();
    await press(fresh);
  }
}
